/**
 * VistoeraTASK-00581 — per-account, per-workspace "last opened canvas project".
 *
 * localStorage only (hint for resume after account switch). The server still
 * authorises every canvas load; a stolen/stale project id must never grant
 * access. Keys are scoped by userId + workspaceId so account A never resumes
 * into account B's project via a shared `lastCanvasId`.
 */

export interface LastOpenedCanvasHint {
  contentProjectId: string
  updatedAt: number
}

const STORAGE_PREFIX = 'vistoera.canvas.lastOpened.v1'

export function lastOpenedCanvasStorageKey(userId: string, workspaceId: string): string {
  return `${STORAGE_PREFIX}.${userId}.${workspaceId}`
}

function storage(): Storage | null {
  try {
    if (typeof globalThis !== 'undefined' && 'localStorage' in globalThis && globalThis.localStorage) {
      return globalThis.localStorage
    }
  } catch {
    // SecurityError in some embedded contexts.
  }
  return null
}

export function rememberLastOpenedCanvas(userId: string, workspaceId: string, contentProjectId: string): void {
  if (!userId || !workspaceId || !contentProjectId) return
  const ls = storage()
  if (!ls) return
  const hint: LastOpenedCanvasHint = { contentProjectId, updatedAt: Date.now() }
  try {
    ls.setItem(lastOpenedCanvasStorageKey(userId, workspaceId), JSON.stringify(hint))
  } catch {
    // Private mode / quota — resume falls back to Home Canvas.
  }
}

export function readLastOpenedCanvas(userId: string, workspaceId: string): string | null {
  if (!userId || !workspaceId) return null
  const ls = storage()
  if (!ls) return null
  try {
    const raw = ls.getItem(lastOpenedCanvasStorageKey(userId, workspaceId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<LastOpenedCanvasHint>
    if (typeof parsed.contentProjectId !== 'string' || !parsed.contentProjectId.trim()) return null
    return parsed.contentProjectId.trim()
  } catch {
    return null
  }
}

/** Drop a stale hint (deleted / forbidden project) so the next visit goes to Home. */
export function clearLastOpenedCanvas(userId: string, workspaceId: string): void {
  if (!userId || !workspaceId) return
  const ls = storage()
  if (!ls) return
  try {
    ls.removeItem(lastOpenedCanvasStorageKey(userId, workspaceId))
  } catch {
    // ignore
  }
}
